// src/components/admin/ProductBlockEditor.tsx
import React, { useState, useEffect } from 'react';
import {
    Plus, Trash2, ChevronUp, ChevronDown, Eye, Save,
    Type, BarChart3, Video, CreditCard, Layers
} from 'lucide-react';
import './ProductBlockEditor.css';
import { ContentBlock, BlockType } from '../../types/contentBlocks.types';

// ✅ IMPORTAR EDITORES DE BLOCOS
import { TextBlockEditor } from './blockEditors/TextBlockEditor';
import { StatsBlockEditor } from './blockEditors/StatsBlockEditor';
import { VideoBlockEditor } from './blockEditors/VideoBlockEditor';
import { FlashcardPreviewBlockEditor } from './blockEditors/FlashcardPreviewBlockEditor';
import { ContentStructureBlockEditor } from './blockEditors/ContentStructureBlockEditor'; // ✅ ADICIONAR


interface ProductBlockEditorProps {
    productId: string;
    onClose: () => void;
    onSave: () => void;
}

export const ProductBlockEditor: React.FC<ProductBlockEditorProps> = ({
    productId,
    onClose,
    onSave
}) => {
    const [blocks, setBlocks] = useState<ContentBlock[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showAddMenu, setShowAddMenu] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    useEffect(() => {
        loadBlocks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId]);

    const loadBlocks = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');

            const response = await fetch(
                `/api/admin/marketplace/products/${productId}/blocks`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.ok) {
                const data = await response.json();
                setBlocks(data.blocks || []);
            }
        } catch (error) {
            console.error('Erro ao carregar blocos:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveBlocks = async () => {
        try {
            setSaving(true);
            const token = localStorage.getItem('accessToken');

            const response = await fetch(
                `/api/admin/marketplace/products/${productId}/blocks`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({ blocks })
                }
            );

            if (response.ok) {
                alert('Blocos salvos com sucesso!');
                onSave();
            } else {
                alert('Erro ao salvar blocos');
            }
        } catch (error) {
            console.error('Erro ao salvar:', error);
            alert('Erro ao salvar blocos');
        } finally {
            setSaving(false);
        }
    };

    const addBlock = (type: BlockType) => {
        const newBlock: ContentBlock = {
            type,
            order: blocks.length,
            data: getDefaultData(type)
        } as ContentBlock;

        setBlocks([...blocks, newBlock]);
        setShowAddMenu(false);
    };

    const getDefaultData = (type: BlockType): any => {
        switch (type) {
            case 'text':
                return { title: 'Novo Título', content: 'Conteúdo aqui...', alignment: 'left' };
            case 'stats':
                return { items: [{ label: 'Total', value: '0', icon: 'package' }] };
            case 'video':
                return { url: '', title: '', description: '' };
            case 'flashcardPreview':
                return { deckId: '', subdeckId: '', cardIds: [] };
            case 'contentStructure': // ✅ ADICIONAR
                return { deckIds: [] };
            default:
                return {};
        }
    };

    const updateBlock = (index: number, updatedBlock: ContentBlock) => {
        const newBlocks = [...blocks];
        newBlocks[index] = updatedBlock;
        setBlocks(newBlocks);
    };

    const deleteBlock = (index: number) => {
        if (window.confirm('Remover este bloco?')) { // ✅ ADICIONAR window.
            const newBlocks = blocks.filter((_, i) => i !== index);
            // Reordenar
            newBlocks.forEach((block, i) => {
                block.order = i;
            });
            setBlocks(newBlocks);
        }
    };

    const moveBlock = (index: number, direction: 'up' | 'down') => {
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === blocks.length - 1) return;

        const newBlocks = [...blocks];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        [newBlocks[index], newBlocks[targetIndex]] = [newBlocks[targetIndex], newBlocks[index]];

        // Atualizar orders
        newBlocks.forEach((block, i) => {
            block.order = i;
        });

        setBlocks(newBlocks);
    };

    const handlePreview = () => {
        // Gerar preview temporário
        const previewId = `preview_${Date.now()}`;
        setPreviewUrl(`/marketplace/${productId}?preview=${previewId}`);
    };

    const renderBlockEditor = (block: ContentBlock, index: number) => {
        switch (block.type) {
            case 'text':
                return (
                    <TextBlockEditor
                        block={block}
                        onChange={(updated) => updateBlock(index, updated)}
                    />
                );
            case 'stats':
                return (
                    <StatsBlockEditor
                        block={block}
                        onChange={(updated) => updateBlock(index, updated)}
                    />
                );
            case 'video':
                return (
                    <VideoBlockEditor
                        block={block}
                        onChange={(updated) => updateBlock(index, updated)}
                    />
                );
            case 'flashcardPreview':
                return (
                    <FlashcardPreviewBlockEditor
                        block={block}
                        onChange={(updated) => updateBlock(index, updated)}
                    />
                );
            case 'contentStructure': // ✅ ADICIONAR
                return (
                    <ContentStructureBlockEditor
                        block={block}
                        onChange={(updated) => updateBlock(index, updated)}
                    />
                );
            default:
                return <div>Tipo de bloco desconhecido</div>;
        }
    };

    const getBlockIcon = (type: BlockType) => {
        switch (type) {
            case 'text': return <Type size={18} />;
            case 'stats': return <BarChart3 size={18} />;
            case 'video': return <Video size={18} />;
            case 'flashcardPreview': return <CreditCard size={18} />;
            case 'contentStructure': return <Layers size={18} />;
            default: return <Plus size={18} />;
        }
    };

    const getBlockLabel = (type: BlockType) => {
        switch (type) {
            case 'text': return 'Texto';
            case 'stats': return 'Estatísticas';
            case 'video': return 'Vídeo';
            case 'flashcardPreview': return 'Preview de Cards';
            case 'contentStructure': return 'Estrutura do Conteúdo';
            default: return 'Desconhecido';
        }
    };

    if (loading) {
        return (
            <div className="pbe-modal-overlay">
                <div className="pbe-modal-content">
                    <div className="pbe-loading">
                        <div className="pbe-spinner"></div>
                        <p>Carregando editor...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="pbe-modal-overlay">
            <div className="pbe-modal-content">
                {/* Header */}
                <div className="pbe-header">
                    <div>
                        <h2>Editor de Conteúdo do Produto</h2>
                        <p className="pbe-subtitle">
                            {blocks.length} bloco(s) configurado(s)
                        </p>
                    </div>
                    <div className="pbe-header-actions">
                        <button
                            className="pbe-btn-preview"
                            onClick={handlePreview}
                            disabled={blocks.length === 0}
                        >
                            <Eye size={18} />
                            Preview
                        </button>
                        <button
                            className="pbe-btn-save"
                            onClick={handleSaveBlocks}
                            disabled={saving}
                        >
                            <Save size={18} />
                            {saving ? 'Salvando...' : 'Salvar'}
                        </button>
                        <button
                            className="pbe-btn-close"
                            onClick={onClose}
                        >
                            ✕
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="pbe-content">
                    {/* Blocks List */}
                    <div className="pbe-blocks-list">
                        {blocks.length === 0 ? (
                            <div className="pbe-empty">
                                <p>Nenhum bloco adicionado ainda</p>
                                <p className="pbe-hint">
                                    Clique em "Adicionar Bloco" para começar
                                </p>
                            </div>
                        ) : (
                            blocks.map((block, index) => (
                                <div key={index} className="pbe-block-item">
                                    {/* Block Header */}
                                    <div className="pbe-block-header">
                                        <div className="pbe-block-title">
                                            {getBlockIcon(block.type)}
                                            <span>{getBlockLabel(block.type)}</span>
                                        </div>
                                        <div className="pbe-block-actions">
                                            <button
                                                className="pbe-btn-icon"
                                                onClick={() => moveBlock(index, 'up')}
                                                disabled={index === 0}
                                                title="Mover para cima"
                                            >
                                                <ChevronUp size={16} />
                                            </button>
                                            <button
                                                className="pbe-btn-icon"
                                                onClick={() => moveBlock(index, 'down')}
                                                disabled={index === blocks.length - 1}
                                                title="Mover para baixo"
                                            >
                                                <ChevronDown size={16} />
                                            </button>
                                            <button
                                                className="pbe-btn-icon pbe-danger"
                                                onClick={() => deleteBlock(index)}
                                                title="Remover"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Block Editor */}
                                    <div className="pbe-block-editor">
                                        {renderBlockEditor(block, index)}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Add Block Button */}
                    <div className="pbe-add-section">
                        <button
                            className="pbe-btn-add"
                            onClick={() => setShowAddMenu(!showAddMenu)}
                        >
                            <Plus size={20} />
                            Adicionar Bloco
                        </button>

                        {showAddMenu && (
                            <div className="pbe-add-menu">
                                <button
                                    className="pbe-add-option"
                                    onClick={() => addBlock('text')}
                                >
                                    <Type size={18} />
                                    <div>
                                        <strong>Texto</strong>
                                        <span>Adicionar texto formatado</span>
                                    </div>
                                </button>

                                <button
                                    className="pbe-add-option"
                                    onClick={() => addBlock('stats')}
                                >
                                    <BarChart3 size={18} />
                                    <div>
                                        <strong>Estatísticas</strong>
                                        <span>Cards, subdecks, horas, etc</span>
                                    </div>
                                </button>

                                <button
                                    className="pbe-add-option"
                                    onClick={() => addBlock('video')}
                                >
                                    <Video size={18} />
                                    <div>
                                        <strong>Vídeo</strong>
                                        <span>Incorporar vídeo do YouTube</span>
                                    </div>
                                </button>

                                <button
                                    className="pbe-add-option"
                                    onClick={() => addBlock('flashcardPreview')}
                                >
                                    <CreditCard size={18} />
                                    <div>
                                        <strong>Preview de Flashcards</strong>
                                        <span>Mostrar cards selecionados</span>
                                    </div>
                                </button>

                                <button
                                    className="pbe-add-option"
                                    onClick={() => addBlock('contentStructure')}
                                >
                                    <Layers size={18} />
                                    <div>
                                        <strong>Estrutura do Conteúdo</strong>
                                        <span>Mostrar decks, módulos e contagens</span>
                                    </div>
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Preview Modal */}
                {previewUrl && (
                    <div className="pbe-preview-modal">
                        <div className="pbe-preview-header">
                            <h3>Preview da Página</h3>
                            <button onClick={() => setPreviewUrl(null)}>✕</button>
                        </div>
                        <iframe
                            src={previewUrl}
                            className="pbe-preview-iframe"
                            title="Preview"
                        />
                    </div>
                )}
            </div>
        </div>
    );
};